"use client";

import Link from "next/link";
import { useEffect, useState, useCallback } from "react";
import { useSession } from "next-auth/react";
import { fetchBalance } from "@/services/billing.service";

/**
 * Small pill in the navbar showing the user's current HOOPS AI credit balance.
 *
 * Polling: refetches when the page regains focus (so chat usage gets reflected
 * without a hard reload). External code can also call `window.dispatchEvent(new
 * CustomEvent('hoops:credits-changed'))` after a redeem / chat-send to trigger
 * an immediate refresh.
 */
export function NavCreditsWidget() {
  const { data: session, status } = useSession();
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const token = session?.user.accessToken;

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const summary = await fetchBalance(token);
      setBalance(summary.balance);
    } catch {
      // Silent — the widget shouldn't error-spam the navbar.
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (status !== "authenticated") return;
    void refresh();
  }, [status, refresh]);

  useEffect(() => {
    if (status !== "authenticated") return;
    const onFocus = () => { void refresh(); };
    const onCustom = () => { void refresh(); };
    window.addEventListener("focus", onFocus);
    window.addEventListener("hoops:credits-changed", onCustom);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("hoops:credits-changed", onCustom);
    };
  }, [status, refresh]);

  // Hide the widget for signed-out users — they don't have a balance.
  if (status !== "authenticated") return null;

  const display = balance === null ? (loading ? "…" : "—") : balance.toLocaleString();
  const isLow = balance !== null && balance > 0 && balance <= 10;
  const isEmpty = balance === 0;

  const color = isEmpty ? "#e05252" : isLow ? "#e0a052" : "var(--text-secondary)";
  const borderColor = isEmpty
    ? "#3a1515"
    : isLow
      ? "#3a2515"
      : "var(--border-subtle)";

  return (
    <Link
      href="/billing"
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] tabular-nums transition-colors duration-150 hover:bg-[var(--surface)]"
      style={{ color, border: `1px solid ${borderColor}` }}
      title={
        isEmpty
          ? "Out of HOOPS AI credits — top up"
          : isLow
            ? `Running low (${display} credits)`
            : `${display} HOOPS AI credits`
      }
      aria-label={`HOOPS AI credits: ${display}`}
    >
      <span style={{ color: "var(--accent)", fontSize: 13, lineHeight: 1 }} aria-hidden="true">◆</span>
      <span aria-live="polite">{display}</span>
    </Link>
  );
}
